import { Component, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription, take } from 'rxjs';
import { APIResponse } from 'fakturnia-shared';
import { ApiService } from 'shared/services/api.service';
import { debounce } from 'lodash'
import { PaginationComponent } from 'shared/components/pagination/pagination.component';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { DateTime } from 'luxon';

@Component({
  standalone: true,
  selector: 'app-users',
  imports: [
    CommonModule, 
    RouterLink, 
    PaginationComponent, 
    ReactiveFormsModule, 
    MatFormFieldModule, 
    MatInputModule, 
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule
  ],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnDestroy {

  private _subscriptions: Subscription[] = []
  searchInputControl = new FormControl()

  pagination = {
    currentPage: 1,
    itemsPerPage: 20,
    count: 0,
    pages: 0
  }

  users: any = []
  isLoading: boolean = false
  displayedColumns: string[] = ['number', 'name', 'email', 'subscription', 'actions']

  constructor(private _apiService: ApiService) {

    this.browseUsers = debounce(this.browseUsers, 300)
    this._subscriptions.push(this.searchInputControl.valueChanges.subscribe({
      next: (value) => {
        this.pagination.currentPage = 1 // Reset do pierwszej strony przy wyszukiwaniu
        this.browseUsers()
      }
    }))

    this.browseUsers()
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe())
  }

  browseUsers() {
    this.isLoading = true
    this._apiService.browseUsers({
      searchQuery: this.searchInputControl.value,
      currentPage: this.pagination.currentPage,
      itemsPerPage: this.pagination.itemsPerPage
    })
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {
          if (response.success == true) {
            this.users = response.data.users
            this.pagination.pages = response.data.pages
            this.pagination.itemsPerPage = response.data.itemsPerPage
            this.pagination.count = response.data.count
          }
          this.isLoading = false
        },
        error: () => {
          this.isLoading = false
        }
      })
  }

  onPageChange(page) {
    this.pagination.currentPage = page
    this.browseUsers()
  }

  getUserNumber(index: number): number {
    return (this.pagination.currentPage - 1) * this.pagination.itemsPerPage + index + 1
  }

  // Subscription helpers
  getSubscriptionTypeLabel(stage: number): string {
    return ['FREE', 'START', 'PRO', 'BUSINESS'][stage] || '-';
  }

  getSubscriptionTypeColor(stage: number): string {
    const colors: Record<number, string> = {
      0: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
      1: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      2: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      3: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    };
    return colors[stage] || 'bg-gray-100 text-gray-700';
  }

  getSubscriptionDaysRemaining(validUntil: string): number {
    if (!validUntil) return 0;
    const until = DateTime.fromISO(validUntil);
    const now = DateTime.now();
    const diff = until.diff(now, 'days');
    return Math.max(0, Math.floor(diff.days));
  }

  formatSubscriptionDate(date: string): string {
    if (!date) return '-';
    return DateTime.fromISO(date).toFormat('dd.MM.yyyy HH:mm');
  }

  isSubscriptionExpired(validUntil: string): boolean {
    if (!validUntil) return true;
    return DateTime.fromISO(validUntil) < DateTime.now();
  }

  getSubscriptionTooltip(user: any): string {
    if (!user.subscription?.validUntil) return 'Brak subskrypcji';
    const days = this.getSubscriptionDaysRemaining(user.subscription.validUntil);
    const date = this.formatSubscriptionDate(user.subscription.validUntil);
    if (this.isSubscriptionExpired(user.subscription.validUntil)) {
      return `Wygasła: ${date}`;
    }
    return `Ważna do: ${date}\nPozostało: ${days} dni`;
  }
}
